import { FC, useCallback, useEffect, useRef, useState } from 'react';
import FocusTrap from 'focus-trap-react';
import useRuntime from '@belong/providers/runtime';
import { SearchPanel } from '@belong/search';
import { COLOURS, FONT_COLOURS } from '@belong/themes';
import { ICON_NAMES, IOrganismMenuItem } from '@belong/types';
import { A11yCopy, Logo as BelongLogo, Icon, KEYBOARD_KEYS, Stack } from '@belong/ui-components';
import { logout } from '@belong/user';
import { NbnCartButton } from '../NbnCartButton';
import { GLOBAL_HEADER_TEST_ID as TEST_ID } from '../testids';
import { OFFCANVAS_LINK_PANEL_A11Y_ID } from '../variables';
import { GHMobileAccountButton } from './GHMobileAccountButton';
import { MobileLinkPanel } from './GHMobileLinkPanel';
import { GlobalBackButton } from './GlobalBackButton.styles';
import { GlobalHeaderMobileMainMenu } from './GlobalHeaderMobileMainMenu';
import { HamburgerMenuIcon } from './HamburgerMenuIcon';
import * as styles from './styles';
import { IGlobalHeaderMobile } from './types';

const SEARCH_TITLE_A11Y_ID = 'search-title-a11y-id';
const SEARCH_PANEL_A11Y_ID = 'search-offcanvas-panel-a11y-id';
const TOP_LEVEL_MENU_A11Y_ID = 'top-level-menu-a11y-id';
type IMenuBarButtons = 'navigation' | 'search' | 'back' | 'close';

export const GlobalHeaderMobile: FC<IGlobalHeaderMobile> = ({
  headerMenuContent,
  currentUrl,
  hideMyAccountLinks,
  isLoggedIn,
  urls,
  onExpanded,
  isNbnOrderCartReady,
  topHeaderRef
}: IGlobalHeaderMobile) => {
  const [showMenu, setShowMenu] = useState<boolean>(false);
  const [showSearch, setShowSearch] = useState<boolean>(false);
  const [showLinkPanel, setShowLinkPanel] = useState<boolean>(false);
  const [removeLinkPanel, setRemoveLinkPanel] = useState<boolean>(false);
  const [linkPanelContent, setLinkPanelContent] = useState<IOrganismMenuItem | undefined>(undefined);
  const [accentOriginRight, setAccentOriginRight] = useState<boolean>(false); // state to update the accent strip position
  const accentOriginRightRef = useRef<boolean>(false); // ref to handle code based decisions on accent strip position
  const offCanvasMenuRef = useRef<HTMLUListElement>(null);
  const offCanvasSearchRef = useRef<HTMLElement>(null);
  const offCanvasLinkPanelRef = useRef<HTMLElement>(null);
  const linkPanelRef = useRef<HTMLElement>(null);
  const accentStripRef = useRef<HTMLElement>(null);
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const searchButtonRef = useRef<HTMLButtonElement>(null);
  const launcherButtonRef = useRef<HTMLButtonElement | null>(null);
  const backButtonRef = useRef<HTMLButtonElement | null>(null);

  const setLauncherButtonRef = (node: HTMLButtonElement | null) => {
    launcherButtonRef.current = node;
  };

  useEffect(() => {
    onExpanded?.(showMenu);
  }, [onExpanded, showMenu]);

  const addTransitionEndListener = (element: HTMLElement | null, callback: () => void): void => {
    element?.addEventListener('transitionend', callback);
  };

  const removeTransitionEndListener = (element: HTMLElement | null, callback: () => void): void => {
    element?.removeEventListener('transitionend', callback);
  };

  const updateMenuUI = (menuBarButton: IMenuBarButtons) => {
    const isSearchButton = menuBarButton === 'search';
    const isNavigationButton = menuBarButton === 'navigation';
    const isCloseButton = menuBarButton === 'close';
    const isBackButton = menuBarButton === 'back';

    if (isNavigationButton) {
      setShowSearch(false);
      setShowMenu(true);
    } else if (isCloseButton) {
      setShowMenu(false);
      setShowLinkPanel(false);
      setLauncherButtonRef(null);
    } else if (isBackButton) {
      setShowMenu(true);
      setShowLinkPanel(false);
      backButtonRef.current?.blur();
      addTransitionEndListener(offCanvasMenuRef.current, () => {
        launcherButtonRef?.current?.focus();
      });
    } else if (isSearchButton) {
      if (showSearch) {
        setShowSearch(false);
      } else {
        setLauncherButtonRef(null);
        setShowSearch(true);
        setShowMenu(false);
        setShowLinkPanel(false);
      }
    }
  };

  const handleMenuBarButtonClick = (menuBarButton: IMenuBarButtons): void => {
    setAccentOriginRight(menuBarButton === 'search');
    accentOriginRightRef.current = menuBarButton === 'search';

    if (!showMenu && !showLinkPanel && !showSearch) {
      // if the accent strip is not on the expected side for the type of menu button clicked then reset it
      // we must use the actual position of the element since the state value is not yet updated

      if (accentStripRef.current) {
        // check on the current position of the accent strip
        const isAccentStripOnLeft = getComputedStyle(accentStripRef.current)
          .getPropertyValue('transform')
          .includes('-');

        // determine if the accent strip needs to be reset into position
        if (
          (isAccentStripOnLeft && menuBarButton === 'search') ||
          (!isAccentStripOnLeft && menuBarButton === 'navigation')
        ) {
          accentStripRef.current.style.opacity = '0';
          accentStripRef.current.style.transition = 'transform 0.1s';
          addTransitionEndListener(accentStripRef.current, resetAccentStrip);
        } else {
          updateMenuUI(menuBarButton);
        }
      }
    } else {
      updateMenuUI(menuBarButton);
    }
  };

  const resetAccentStrip = (): void => {
    removeTransitionEndListener(accentStripRef.current, resetAccentStrip);
    if (accentStripRef.current) {
      accentStripRef.current.style.removeProperty('opacity');
      accentStripRef.current.style.removeProperty('transition');
    }

    updateMenuUI(accentOriginRightRef.current ? 'search' : 'navigation');
  };

  const handleShowLinkPanel = (menuItem: IOrganismMenuItem): void => {
    addTransitionEndListener(offCanvasLinkPanelRef.current, focusLinkPanelHeading);
    // reset the opacity. It may previously have been set to 0
    if (offCanvasLinkPanelRef.current) {
      offCanvasLinkPanelRef.current.style.removeProperty('opacity');
    }

    // update the content of the second level menu panel
    setLinkPanelContent(menuItem);
    // animate in the second level menu panel
    setShowLinkPanel(true);
    // animate out the main menu panel
    setShowMenu(false);
  };

  const focusLinkPanelHeading = (): void => {
    removeTransitionEndListener(offCanvasLinkPanelRef.current, focusLinkPanelHeading);
    linkPanelRef.current?.focus();
  };

  const handleHideLinksPanel = (): void => {
    menuButtonRef.current?.focus();
    setLauncherButtonRef(null);
    addTransitionEndListener(offCanvasLinkPanelRef.current, resetLinkPanel);
    setRemoveLinkPanel(true);
    setShowLinkPanel(false);
  };

  const resetLinkPanel = (): void => {
    removeTransitionEndListener(offCanvasLinkPanelRef.current, resetLinkPanel);
    // set the opacity to conceal the resetting animation
    if (offCanvasLinkPanelRef.current) {
      offCanvasLinkPanelRef.current.style.opacity = '0';
    }

    setRemoveLinkPanel(false);
  };

  const handleMenuClose = useCallback((): void => {
    setShowMenu(false);
    setLauncherButtonRef(null);
    menuButtonRef.current?.focus();
  }, [setShowMenu, setLauncherButtonRef]);

  const handleMenuEscape = useCallback(({ key }: KeyboardEvent): boolean | void => {
    removeTransitionEndListener(offCanvasMenuRef.current, () => {});
    if (key === KEYBOARD_KEYS.ESCAPE) {
      handleMenuClose();
    }
  }, []);

  const handleLinkPanelEscape = useCallback(({ key }: KeyboardEvent): boolean | void => {
    removeTransitionEndListener(offCanvasMenuRef.current, () => {});
    if (key === KEYBOARD_KEYS.ESCAPE) {
      setShowLinkPanel(false);
    }
  }, []);

  const handleSearchEscape = useCallback(({ key }: KeyboardEvent): boolean | void => {
    removeTransitionEndListener(offCanvasSearchRef.current, () => {});
    if (key === KEYBOARD_KEYS.ESCAPE) {
      setShowSearch(false);
    }
  }, []);

  const { isDesktopNav } = useRuntime();

  useEffect(() => {
    if (isDesktopNav) {
      handleMenuClose();
      setShowLinkPanel(false);
      setShowSearch(false);
    }
  }, [handleMenuClose, isDesktopNav]);

  // this useEffect is used to position to the off canvas panels
  useEffect(() => {
    if (onExpanded !== undefined) {
      onExpanded(showMenu || showLinkPanel || showSearch);
    }

    if (offCanvasMenuRef?.current) {
      if (topHeaderRef?.current) {
        const headerEndPos = topHeaderRef?.current.getBoundingClientRect().bottom;
        offCanvasMenuRef.current.style.top = `${headerEndPos}px`;
      }

      window.addEventListener('keydown', handleMenuEscape);
    }

    if (offCanvasLinkPanelRef?.current) {
      if (topHeaderRef?.current) {
        const headerEndPos = topHeaderRef?.current.getBoundingClientRect().bottom;
        offCanvasLinkPanelRef.current.style.top = `${headerEndPos}px`;
      }

      window.addEventListener('keydown', handleLinkPanelEscape);
    }

    if (offCanvasSearchRef?.current) {
      if (topHeaderRef?.current) {
        const headerEndPos = topHeaderRef?.current.getBoundingClientRect().bottom;
        offCanvasSearchRef.current.style.top = `${headerEndPos}px`;
      }

      window.addEventListener('keydown', handleSearchEscape);
    }

    return (): void => {
      window.removeEventListener('keydown', handleMenuEscape);
      window.removeEventListener('keydown', handleLinkPanelEscape);
      window.removeEventListener('keydown', handleSearchEscape);
    };
  }, [handleLinkPanelEscape, handleMenuEscape, handleSearchEscape, onExpanded, showLinkPanel, showMenu, showSearch]);

  const handleLogoutClick = (): void => {
    handleMenuClose();
    logout('/');
  };

  /**
   * This effect is a bandaid fix for Safari on iOS *intermittantly* failing
   * to animate `position: fixed` elements properly.
   */
  useEffect(() => {
    const onTransitionEnd = () => {
      if (offCanvasMenuRef.current && accentStripRef.current) {
        // force these elements to repaint
        offCanvasMenuRef.current.style.display = 'none';
        accentStripRef.current.style.display = 'none';

        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
        offCanvasMenuRef.current.offsetHeight;
        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
        accentStripRef.current.offsetHeight;

        offCanvasMenuRef.current.style.display = 'flex';
        accentStripRef.current.style.display = 'flex';
      }
    };

    offCanvasMenuRef.current?.addEventListener('transitionend', onTransitionEnd);

    return () => offCanvasMenuRef.current?.removeEventListener('transitionend', onTransitionEnd);
  }, [offCanvasMenuRef, accentStripRef]);

  return (
    <styles.Root>
      <styles.RelativeContainer data-testid={TEST_ID.MOBILE} aria-label={headerMenuContent.a11ySite}>
        <FocusTrap active={showMenu || showSearch || showLinkPanel}>
          <styles.WrapperModal>
            <styles.HeaderBarMobile>
              <styles.HeaderBarLeftArea>
                <styles.MenuButton
                  onClick={() => {
                    if (showMenu || showLinkPanel) {
                      handleMenuBarButtonClick('close');
                    } else {
                      handleMenuBarButtonClick('navigation');
                    }
                  }}
                  ref={menuButtonRef}
                  aria-expanded={showMenu || showLinkPanel}
                  aria-label={headerMenuContent.a11yMobileMenuLink}
                  aria-controls={TOP_LEVEL_MENU_A11Y_ID}
                  data-testid={TEST_ID.MOBILE_MENU_HAMBURGER}
                >
                  <HamburgerMenuIcon open={showMenu || showLinkPanel} />
                </styles.MenuButton>
                <styles.OffCanvasMenu
                  data-testid={TEST_ID.OFFCANVAS}
                  data-body-scroll={showMenu ? 'locked' : ''}
                  aria-hidden={!showMenu}
                  isExpanded={showMenu}
                  ref={offCanvasMenuRef}
                  aria-label="Menu"
                  tabIndex={showMenu ? -1 : undefined}
                  id={TOP_LEVEL_MENU_A11Y_ID}
                >
                  <GlobalHeaderMobileMainMenu
                    isLoggedIn={isLoggedIn}
                    hideMyAccountLinks={hideMyAccountLinks}
                    currentUrl={currentUrl}
                    urls={urls}
                    headerMenuContent={headerMenuContent}
                    handleOffCanvasClose={handleMenuClose}
                    handleShowLinkPanel={handleShowLinkPanel}
                    setLauncherButtonRef={setLauncherButtonRef}
                  />

                  {/* Log in & Log out */}
                  <GHMobileAccountButton
                    handleLogoutClick={handleLogoutClick}
                    handleClose={handleMenuClose}
                    isLoggedIn={isLoggedIn}
                    urls={urls}
                    loginLabel={headerMenuContent.loginLabel}
                    logoutLabel={headerMenuContent.logoutLabel}
                    loginIcon={headerMenuContent.loginIcon}
                    logoutIcon={headerMenuContent.logoutIcon}
                    currentUrl={currentUrl}
                  />
                </styles.OffCanvasMenu>
                <styles.OffCanvasLinkPanel
                  data-testid={TEST_ID.OFFCANVASLINKPANEL}
                  aria-hidden={!showLinkPanel}
                  data-body-scroll={showLinkPanel ? 'locked' : ''}
                  isCenter={showLinkPanel}
                  offCanvasLeft={removeLinkPanel}
                  ref={offCanvasLinkPanelRef}
                  tabIndex={showLinkPanel ? -1 : undefined}
                  aria-labelledby={OFFCANVAS_LINK_PANEL_A11Y_ID}
                >
                  <Stack space="xsmall">
                    <GlobalBackButton
                      label="Back"
                      iconName={ICON_NAMES.ChevronLeft}
                      linkRef={backButtonRef}
                      onClick={() => {
                        handleMenuBarButtonClick('back');
                      }}
                      overrideColor={FONT_COLOURS.ACTIVE}
                    />

                    <MobileLinkPanel
                      linkPanelContent={linkPanelContent}
                      currentUrl={currentUrl}
                      onLinkClick={() => handleHideLinksPanel()}
                      ref={linkPanelRef}
                    />
                  </Stack>
                </styles.OffCanvasLinkPanel>
              </styles.HeaderBarLeftArea>
              <styles.HeaderBarMiddleArea>
                <styles.LinkLogo
                  href={headerMenuContent.belongHome.href}
                  onClick={handleMenuClose}
                  data-testid={TEST_ID.BUTTON_HOME}
                >
                  <BelongLogo />
                  <A11yCopy>{headerMenuContent.belongHome.label}</A11yCopy>
                </styles.LinkLogo>
              </styles.HeaderBarMiddleArea>
              <styles.HeaderBarRightArea>
                <styles.SearchButton
                  onClick={(): void => handleMenuBarButtonClick('search')}
                  ref={searchButtonRef}
                  aria-expanded={showSearch}
                  aria-label={headerMenuContent.a11ySearchMenu}
                  data-testid={TEST_ID.MOBILE_MENU_SEARCH_BUTTON}
                  aria-controls={SEARCH_PANEL_A11Y_ID}
                >
                  {showSearch ? (
                    <Icon name={ICON_NAMES.Close} hasColor={COLOURS.BELONG_BLUE} />
                  ) : (
                    <Icon name={ICON_NAMES.Search} hasColor={COLOURS.BELONG_BLUE} />
                  )}
                </styles.SearchButton>
                <NbnCartButton
                  menuLabel={headerMenuContent.nbnCartMenuLabel}
                  cartIcon={headerMenuContent.nbnCartIcon}
                  a11yLabel={headerMenuContent.a11yNbnCart}
                  isNbnOrderCartReady={isNbnOrderCartReady}
                  data-testid={TEST_ID.BUTTON_NBN_CART}
                />
              </styles.HeaderBarRightArea>
            </styles.HeaderBarMobile>
            <styles.OffCanvasSearch
              data-testid={TEST_ID.OFFCANVAS_SEARCH}
              aria-hidden={!showSearch}
              isExpanded={showSearch}
              ref={offCanvasSearchRef}
              tabIndex={showSearch ? -1 : undefined}
              aria-labelledby={SEARCH_TITLE_A11Y_ID}
              id={SEARCH_PANEL_A11Y_ID}
            >
              <styles.StyledSearch>
                <SearchPanel
                  callback={() => handleMenuBarButtonClick('search')}
                  data-testid="mobile-search-panel"
                  id={SEARCH_TITLE_A11Y_ID}
                />
              </styles.StyledSearch>
            </styles.OffCanvasSearch>
          </styles.WrapperModal>
        </FocusTrap>
        <styles.AccentStrip
          isExpanded={showMenu || showSearch || showLinkPanel}
          originRight={accentOriginRight}
          ref={accentStripRef}
        />
      </styles.RelativeContainer>
    </styles.Root>
  );
};

GlobalHeaderMobile.displayName = 'GlobalHeaderMobile';
